import { userActionTypes } from '../actions/userActions.js';
import { SERVICE_TYPE } from '../../../constant.js';
import { userCookie } from '~/models/UserCookie';
import { deleteAllCookies } from '~/store/storage.js';

/**
 * @type {UserState.State}
 */
export const initState = {
  user: null,
  userLoaded: false,
  userLoadedFailed: false,

  tokenType: null,
  accessToken: null,
  createdAt: null,

  loginSuccess: false,
  loginFailed: false,
  loginError: null,

  refreshingToken: false,
  refreshTokenSuccess: false,
  refreshTokenFailed: false,

  stop: null,

  address: '',
  geoLocation: null,
  addressId: null,
  addressError: null,
  serviceRegionId: null,
  serviceType: SERVICE_TYPE.DELIVERY,

  creditAdditions: [],
  creditDeductions: [],
  creditsLoaded: false,
  creditsLoadError: false,

  lifeTimeSaving: [],
  lifeTimeSavingloaded: false,
  lifeTimeSavingloadedFailed: false,

  referralCode: {
    code: null,
    freeDelivery: null,
    referralReward: null,
    amount: null,
  },
  referralCodeLoaded: false,
  referralCodeLoadedFailed: false,

  notifications: [],
  notificationsLoaded: false,

  //verification code
  codeSent: false,
};

const handleUpdateUserSearchAddress = (state, action) => {
  const { geoLocation, serviceRegionId, address } = action;
  userCookie.address = address;
  userCookie.geoLocation = geoLocation;
  userCookie.serviceRegionId = serviceRegionId;
  return {
    ...state,
    ...{
      address,
      geoLocation,
      serviceRegionId,
      addressId: null,
    },
  };
};

const handleSelectUserAddress = (state, action) => {
  const { address, addressId, geoLocation, serviceRegionId } = action;
  userCookie.address = address;
  userCookie.geoLocation = geoLocation;
  userCookie.serviceRegionId = serviceRegionId;
  return {
    ...state,
    address,
    addressId,
    geoLocation,
    serviceRegionId,
  };
};

const handleUserDataSuccess = (state, action, newState = {}) => {
  const { tokenType, accessToken, refreshToken, createdAt, expires_in, email } = action;
  const expireTime = new Date().getTime() + (expires_in - 30) * 1000;
  userCookie.accessToken = accessToken;
  userCookie.createdAt = createdAt;
  userCookie.email = email;
  userCookie.expireTime = expireTime;
  userCookie.refreshToken = refreshToken;
  userCookie.tokenType = tokenType;
  return {
    ...state,
    accessToken,
    createdAt,
    email,
    expireTime,
    refreshToken,
    tokenType,
    ...newState,
  };
};

const handleUserLoginSuccess = (state, action) => {
  return handleUserDataSuccess(state, action, {
    loginSuccess: true,
    loginFailed: false,
  });
};

const handleRefreshTokenSuccess = (state, action) => {
  return handleUserDataSuccess(state, action, {
    refreshingToken: false,
    refreshTokenSuccess: true,
    refreshTokenFailed: false,
  });
};

const handleUserDataFailed = (state, newState = {}) => {
  userCookie.logout();
  // just to be safe, delete everything
  deleteAllCookies();
  return {
    ...state,
    tokenType: null,
    accessToken: null,
    createdAt: null,
    ...newState,
  };
};

const handleUserLoginFailed = (state) => {
  return handleUserDataFailed(state, {
    loginSuccess: false,
    loginFailed: true,
  });
};

const handleRefreshTokenFailed = (state) => {
  return handleUserDataFailed(state, {
    refreshingToken: false,
    refreshTokenSuccess: false,
    refreshTokenFailed: true,
  });
};

const handleSetUserServiceType = (state, action) => {
  const { serviceType } = action;
  userCookie.serviceType = serviceType;
  return { ...state, ...{ serviceType } };
};

const handleGetUserCreditesSuccess = (state, { creditAdditions, creditDeductions }) => {
  return {
    ...state,
    ...{
      creditsLoaded: true,
      creditsLoadError: false,
      creditAdditions,
      creditDeductions,
    },
  };
};

const handleGetUserCreditesFailed = (state) => {
  return {
    ...state,
    ...{
      creditsLoaded: true,
      creditsLoadError: true,
    },
  };
};

const handleGetUserReferralCodeSuccess = (state, action) => {
  const { referral_code } = action;
  return {
    ...state,
    ...{
      referralCodeLoaded: true,
      referralCodeLoadedFailed: false,
      referralCode: {
        code: referral_code.code,
        freeDelivery: referral_code.free_delivery,
        referralReward: parseInt(referral_code.referral_reward, 10) || 0,
        amount: parseInt(referral_code.amount, 10) || 0,
      },
    },
  };
};

const handleGetUserReferralCodeFailed = (state) => {
  return {
    ...state,
    ...{
      referralCodeLoaded: true,
      referralCodeLoadedFailed: true,
      referralCode: {
        code: null,
        freeDelivery: null,
        referralReward: null,
        amount: null,
      },
    },
  };
};

const handleGetUserMembershipSavingSuccess = (state, action) => {
  return {
    ...state,
    ...{
      lifeTimeSaving: action.lifeTimeSaving,
      lifeTimeSavingloaded: true,
      lifeTimeSavingloadedFailed: false,
    },
  };
};

const handleGetUserMembershipSavingFailed = (state) => {
  return {
    ...state,
    ...{
      lifeTimeSaving: null,
      lifeTimeSavingloaded: true,
      lifeTimeSavingloadedFailed: true,
    },
  };
};

const reduce_PersistNewUserAddress_success = (state, action) => {
  const { address } = action;

  /* state is UserStore */
  const { user } = state;
  const newUser = {
    ...user,
    addresses: [...user.addresses, address],
  };
  userCookie.address = address;
  return {
    ...state,
    ...{
      addressId: address.id,
      address,
      user: newUser,
    },
  };
};

const handleUpdateUserAddressSuccess = (state, action) => {
  const { address, newAddressId } = action;
  /* state is UserStore */
  const { user, addressId, address: existingAddress } = state;
  const addresses = user.addresses
    .filter((addr) => addr.id !== address.id)
    .concat({
      ...address,
      ...{
        id: newAddressId,
      },
    });
  const newUser = {
    ...user,
    ...{
      addresses,
    },
  };
  if (addressId === address.id) {
    userCookie.address = address;
  }
  return {
    ...state,
    ...{
      user: newUser,
      addressId: String(addressId) === String(address.id) ? newAddressId : addressId,
      address: String(addressId) === String(address.id) ? address : existingAddress,
    },
  };
};

const handleDeleteUserAddressSuccess = (state, action) => {
  /* state is UserStore */
  const { user, addressId } = state;
  const { address } = action;
  const newUser = {
    ...user,
    ...{
      addresses: user.addresses.filter((addr) => addr.id !== address.id),
    },
  };
  return {
    ...state,
    ...{
      user: newUser,
      addressId: String(addressId) === String(address.id) ? null : addressId,
    },
  };
};

const handleUserLogout = () => {
  userCookie.logout();
  return initState;
};

const handleUpdateUser = (state, fields) => {
  /* state is UserStore */
  const { user } = state;
  const newUser = { ...user, ...fields };
  return {
    ...state,
    ...{
      user: newUser,
    },
    serviceRegionId: newUser.service_region_id,
  };
};

const handleSendUserPhoneVerificationSuccess = (state, action) => {
  const newState = handleUpdateUser(state, { phone_number: action.phoneNumber });
  return {
    ...newState,
    ...{
      codeSent: true,
    },
  };
};

const handleVerifyUserSuccess = (state) => {
  return handleUpdateUser(state, { verified: true });
};

/**

 * @param {UserState.State} state
 * @param {*} action
 */
const userReducer = (state = initState, action) => {
  switch (action.type) {
    case userActionTypes.init:
      return {
        ...state,
        ...{
          address: action.address,
          geoLocation: action.geoLocation,
          addressId: action.addressId,
          accessToken: action.accessToken,
          tokenType: action.tokenType,
          createdAt: action.createdAt,
          serviceRegionId: action.serviceRegionId,
        },
      };

    case userActionTypes.updateUserSearchAddress:
      return handleUpdateUserSearchAddress(state, action);

    case userActionTypes.selectUserAddress:
      return handleSelectUserAddress(state, action);

    case userActionTypes.loginSuccess:
      return handleUserLoginSuccess(state, action);

    case userActionTypes.loginFailed:
      return handleUserLoginFailed(state);

    case userActionTypes.refreshingTokenStart:
      return {
        ...state,
        ...{ refreshingToken: true, refreshTokenSuccess: false, refreshTokenFailed: false },
      };

    case userActionTypes.refreshTokenSuccess:
      return handleRefreshTokenSuccess(state, action);

    case userActionTypes.refreshTokenFailed:
      return handleRefreshTokenFailed(state);

    case userActionTypes.getUserStart:
      return { ...state, ...{ user: null, userLoaded: false, userLoadedFailed: false } };

    case userActionTypes.getUserSuccess:
      return {
        ...state,
        ...{
          user: action.user,
          userLoaded: true,
          userLoadedFailed: false,
          serviceRegionId: action.user.service_region_id,
        },
      };

    case userActionTypes.getUserFailed:
      return { ...state, ...{ user: null, userLoaded: false, userLoadedFailed: true } };

    case userActionTypes.setUserServiceType:
      return handleSetUserServiceType(state, action);

    case userActionTypes.getCreditStart:
      return {
        ...state,
        ...{
          creditsLoaded: false,
          creditsLoadError: false,
        },
      };

    case userActionTypes.getCreditSuccess:
      return handleGetUserCreditesSuccess(state, action);

    case userActionTypes.getCreditFailed:
      return handleGetUserCreditesFailed(state, action);

    case userActionTypes.getReferralCodeSuccess:
      return handleGetUserReferralCodeSuccess(state, action);

    case userActionTypes.getReferralCodeFailed:
      return handleGetUserReferralCodeFailed(state);

    case userActionTypes.getMembershipSavingSuccess:
      return handleGetUserMembershipSavingSuccess(state, action);

    case userActionTypes.getMembershipSavingFailed:
      return handleGetUserMembershipSavingFailed(state, action);

    case userActionTypes.persistNewUserAddress_success:
      return reduce_PersistNewUserAddress_success(state, action);

    case userActionTypes.updateUserAddressSuccess:
      return handleUpdateUserAddressSuccess(state, action);

    case userActionTypes.deleteUserAddressSuccess:
      return handleDeleteUserAddressSuccess(state, action);

    case userActionTypes.setUserAddressError:
      return {
        ...state,
        ...{
          addressError: action.error,
        },
      };

    case userActionTypes.getUserNotificationStart:
      return {
        ...state,
        ...{
          notificationsLoaded: false,
        },
      };

    case userActionTypes.getUserNotificationSuccess:
      return {
        ...state,
        ...{
          notifications: action.notifications,
          notificationsLoaded: true,
        },
      };

    case userActionTypes.updateUserSuccess:
      return handleUpdateUser(state, action.user);

    case userActionTypes.sendUserPhoneVerificationSuccess:
      return handleSendUserPhoneVerificationSuccess(state, action);

    case userActionTypes.verifyUserSuccess:
      return handleVerifyUserSuccess(state);

    case userActionTypes.logout:
      return handleUserLogout();

    default:
      return state;
  }
};

export default userReducer;
